/**
 * MachineCard — Status card for the fleet overview grid.
 *
 * Shows machine name, location, online/offline badge, any active alerts, and
 * last-seen time. Live hardware/cash detail lives on the machine page (Core
 * Status) — the legacy per-snapshot toner/paper/cashbox view was retired with
 * the v2 Core migration (OPS-20).
 */

import { Link } from 'react-router-dom';
import type { Machine, FeedbackTotals } from '../api/client';
import { StatusBadge } from './StatusBadge';
import { FeedbackBar } from './FeedbackBar';

interface MachineCardProps {
  machine: Machine;
  alertInfo?: { count: number; types: string[] };
  /** Отзывы за 30 дней; нет — машине никто не ставил оценку. */
  feedback?: FeedbackTotals;
}

function formatTimeAgo(dateStr: string): string {
  // Tolerate Postgres timestamptz ("… +00"): swap space→T and pad the bare
  // offset so Date() doesn't return Invalid (→ "NaNd ago").
  const t = new Date(String(dateStr).replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00')).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function MachineCard({ machine, alertInfo, feedback }: MachineCardProps) {
  return (
    <Link to={`/machines/${machine.machineId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 150ms ease' }}>
        <div className="card-body">
          {/* Header: name + status */}
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                {machine.name}
                {/* Песочницу подписываем явно: её заказы не идут в выручку,
                    и оператор не должен путать стенд с боевым автоматом. */}
                {machine.isSandbox && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 6px',
                    borderRadius: 4, background: 'rgba(234,179,8,0.18)', color: '#a16207',
                  }}>
                    SANDBOX
                  </span>
                )}
                {/* Выведен из работы из админки: на киоске экран «mimo provoz». */}
                {machine.outOfService && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 6px',
                    borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#b91c1c',
                  }}>
                    OUT OF SERVICE
                  </span>
                )}
              </div>
              {/* То, из-за чего киоск встаёт: бумага, принтеры, экран.
                  Показываем только проблемы — здоровая машина не должна
                  тонуть в зелёных галочках. */}
              {machine.live && <LiveChips live={machine.live} />}
              {machine.location && (
                <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                  {machine.location}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {alertInfo && alertInfo.count > 0 && (
                <span
                  className="badge"
                  style={{
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    fontSize: '0.72rem',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontWeight: 600,
                  }}
                  title={alertInfo.types.join(', ')}
                >
                  {alertInfo.count} alert{alertInfo.count > 1 ? 's' : ''}
                </span>
              )}
              <StatusBadge online={machine.isOnline} />
            </div>
          </div>

          {feedback && feedback.total > 0 && (
            <div style={{ marginBottom: 6 }}>
              <FeedbackBar totals={feedback} compact />
            </div>
          )}

          {/* Last seen */}
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            {machine.lastSeenAt
              ? `Last seen: ${formatTimeAgo(machine.lastSeenAt)}`
              : 'Never connected'}
          </div>
        </div>
      </div>
    </Link>
  );
}

/** Короткие пометки о проблемах железа. Всё в порядке — ничего не рисуем. */
function LiveChips({ live }: { live: NonNullable<import('../api/client').Machine['live']> }) {
  const chips: Array<{ text: string; critical?: boolean }> = [];
  if (live.paperOutBw) chips.push({ text: 'no paper B&W', critical: true });
  if (live.paperOutColor) chips.push({ text: 'no paper colour', critical: true });
  // Склад бумаги из сервис-меню: показываем, только когда мало (порог машины
  // живёт на сервере; здесь грубая планка для карточки).
  if (!live.paperOutBw && typeof live.paperBwStock === 'number' && live.paperBwStock < 200) chips.push({ text: `paper B&W ${live.paperBwStock}`, critical: live.paperBwStock < 50 });
  if (!live.paperOutColor && typeof live.paperColorStock === 'number' && live.paperColorStock < 100) chips.push({ text: `paper colour ${live.paperColorStock}`, critical: live.paperColorStock < 50 });
  if (live.printerBw && live.printerBw !== 'online') chips.push({ text: `B&W ${live.printerBw}`, critical: true });
  if (live.printerColor && live.printerColor !== 'online') chips.push({ text: `colour ${live.printerColor}`, critical: true });
  if (live.uiConnected === false) chips.push({ text: 'screen down', critical: true });
  // Тонер — единственный расходник с настоящим процентом: показываем, только
  // когда он уже низкий, иначе карточка зарастёт цифрами.
  if (typeof live.tonerMinPercent === 'number' && live.tonerMinPercent <= 20) {
    chips.push({ text: `toner ${live.tonerMinPercent}%`, critical: live.tonerMinPercent <= 10 });
  }
  if (live.hopperLow) chips.push({ text: 'low coins' });
  if (live.ups && live.ups !== 'mains') chips.push({ text: `UPS ${live.ups}` });

  if (chips.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
      {chips.map((c) => (
        <span key={c.text} style={{
          fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
          background: c.critical ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.18)',
          color: c.critical ? '#b91c1c' : '#a16207',
        }}>
          {c.text}
        </span>
      ))}
    </div>
  );
}
